import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';

import { CreateEntityCommandService } from '../create-entity-command.service';
import { CreateEntityReferenceDataService } from '../create-entity-reference-data.service';
import { CreateEntityWizardStore } from '../create-entity-wizard.store';
import { STUDY_FORM_SCHEMA } from './study-form.schema-validators';
import { DEFAULT_STUDY_FORM_VALUE, STUDY_FORM_KIND } from './study-form.types';


/**
 * Study Details form, extracted from `CreateEntityElementComponent` so each
 * entity tab owns its own form, mirroring `OrganizationFormComponent` and
 * `FacilityFormComponent`.
 *
 * Study statuses are fetched from the real Jemma API (`JemmaStudyApiService.getStudyStatuses()`),
 * gated the same way the Organization and Facility tabs gate their reference-data fetch. A study
 * has no address or geolocation in the real Jemma API — Study Label and Study Description are
 * optional free-text fields; everything else mirrors `StudyCreateRequestBody`.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'wcg-study-form',
    imports: [FormField, MatFormField, MatSelect, MatOption],
    templateUrl: './study-form.component.html'
})
export class StudyFormComponent {
    private readonly _commands = inject(CreateEntityCommandService);
    private readonly _wizard = inject(CreateEntityWizardStore);
    private readonly _referenceData = inject(CreateEntityReferenceDataService);

    /** Whether this panel should be visually hidden (the "inactive" tabs stay in the DOM so their data is never lost). */
    readonly hidden = input(false);

    readonly $studyStatuses = this._referenceData.$studyStatuses;
    readonly $referenceDataError = this._referenceData.$studyStatusesError;

    /** Typed Signal Form, mirroring the Organization and Facility tabs. */
    readonly $model = signal({ ...DEFAULT_STUDY_FORM_VALUE });
    readonly $form = form(this.$model, STUDY_FORM_SCHEMA);

    constructor() {
        /** "Save" was pressed while this tab was active — see `CreateEntityWizardStore` for why every `*FormComponent` reacts to its own tab's command instead of the parent holding a `viewChild` reference. */
        effect(() => {
            const _request = this._wizard.submitRequest();
            if (_request?.tab !== STUDY_FORM_KIND) {
                return;
            }

            untracked(() => this._trySubmit());
        });

        /** "+ Add Another" was pressed after this tab's entity was just created. */
        effect(() => {
            const _request = this._wizard.resetRequest();
            if (_request?.tab !== STUDY_FORM_KIND) {
                return;
            }

            untracked(() => this.reset());
        });
    }

    /** Triggered by the native `ngSubmit` when the user presses Enter inside the form; the "Save" button instead goes through `CreateEntityWizardStore.requestSubmit()`. */
    onSubmit(): void {
        this._trySubmit();
    }

    /** Resets the form back to its blank initial state (used by the "+ Add Another" flow). */
    reset(): void {
        this.$form().reset({ ...DEFAULT_STUDY_FORM_VALUE });
    }

    private _trySubmit(): void {
        if (this._wizard.submitting()) {
            return;
        }

        if (this.$form().invalid()) {
            this.$form().markAsTouched();
            return;
        }

        this._commands.createStudy(this.$form().value());
    }
}
